import { Address, extractAddress } from "./address";
import { isStreetNumberMissing } from "./addressValidation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../hooks/use-theme";
import { ActivityIndicator, Pressable, StyleSheet, TextInput, View } from "react-native";
import Constants from "expo-constants";
import { GooglePlacesAutocomplete } from "./GooglePlacesAutocomplete";
import SnackbarZone from "../../components/SnackbarZone";
import {
    GooglePlaceData,
    GooglePlaceDetail,
    GooglePlacesAutocompleteRef,
} from "react-native-google-places-autocomplete";
import ListItem from "../../ui_atoms/ListItem";
import Modal from "../Modal";
import Icon from "../Icon";
import TextField, { TextFieldProps } from "../TextField";
import { useI18n } from "../../hooks/i18n-hooks";
import Gap from "../../ui_atoms/Gap";
import { useSnackbar } from "../../hooks/snackbar";

export type AddressChooserProps = {
    language?: string;
    onSelect?: (address: Address) => void;
    onDismiss: () => void;
    visible: boolean;
    inputPlaceholder?: string;
    inputLabel?: string;
    allowedCountries?: string[]; // maximum 5 countries :https://developers.google.com/maps/documentation/places/web-service/autocomplete?_gl=1*1pabl58*_up*MQ..*_ga*NzcxOTA0ODk1LjE3Mjc4NjE1Njc.*_ga_NRWSTWS78N*MTcyNzg2MTU2Ny4xLjAuMTcyNzg2MTY0My4wLjAuMA..
    initialValue?: string;
    requiresPreciseAddress?: boolean;
};

type HeaderFieldProps = {
    onDismiss: () => void;
    isLocationLoading: boolean;
    onLocation: () => Promise<void>;
    isIncompleteAddress?: boolean;
    onChangeTextExternal?: () => void;
} & Pick<TextFieldProps, "onChangeText" | "testID">;

// Wait for a pause in typing before querying Google Places. Without it every keystroke fires (and
// immediately aborts) a billed autocomplete request, which on Android also floods the JS thread with
// renders while the user is still typing.
const SEARCH_DEBOUNCE_MS = 250;

export const ADDRESS_SEARCH_INPUT_TEST_ID = "address-search-input";

const HeaderField = ({
    onDismiss,
    isLocationLoading,
    onLocation,
    isIncompleteAddress,
    onChangeTextExternal,
    ref: InputRef,
    ...props
}: HeaderFieldProps & { ref?: React.Ref<TextInput> }) => {
    const style = useHeaderStyle();
    const i18n = useI18n();

    return (
        <View style={style.wrapper}>
            <View style={style.container}>
                <Pressable onPress={onDismiss} style={style.backButton}>
                    <Icon name="close" size={28} />
                </Pressable>
                <View style={style.textFieldWrapper}>
                    <TextField
                        {...props}
                        ref={InputRef}
                        clearButtonMode="never"
                        style={style.textField}
                        onChangeText={(text) => {
                            props.onChangeText?.(text);
                            onChangeTextExternal?.();
                        }}
                        error={
                            isIncompleteAddress
                                ? i18n.t(
                                      "screens.user.create_mission_working_address.incomplete_address",
                                  )
                                : undefined
                        }
                    />
                </View>
            </View>
            <Gap gap={0.5} />
            <ListItem
                {...(isLocationLoading
                    ? {
                          iconComponent: (
                              <ActivityIndicator size={24} />
                          ),
                          disabled: true,
                      }
                    : { icon: "map-marker" })}
                rightActionMode="empty"
                description={i18n.t("your_location")}
                onPress={onLocation}
            />
        </View>
    );
};

const useHeaderStyle = () => {
    const { gridSize, marginSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                wrapper: {
                    flexDirection: "column",
                    flexShrink: 1,
                    width: "100%",
                },
                container: {
                    marginHorizontal: marginSize,
                    flexDirection: "row",
                    alignItems: "center",
                },
                backButton: {
                    flexGrow: 0,
                    flexShrink: 1,
                    width: 28,
                },
                textFieldWrapper: {
                    flex: 1,
                    marginLeft: gridSize / 2,
                },
                textField: {
                    backgroundColor: "white",
                    flexShrink: 1,
                },
            }),
        [gridSize, marginSize],
    );
};

if (process.env.NODE_ENV !== "test") {
    // not working on web, but works on native
    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        navigator.geolocation = require("@react-native-community/geolocation");
    } catch (e) {
        console.warn("Geolocation not available", e);
    }
}

export const AddressChooser: React.FC<AddressChooserProps> = ({
    language = "en",
    onSelect,
    onDismiss,
    visible,
    inputLabel,
    inputPlaceholder,
    allowedCountries,
    initialValue = "",
    requiresPreciseAddress = false,
}) => {
    const { showSnackbar } = useSnackbar();
    const { gridSize, marginSize } = useTheme();
    const ref = useRef<GooglePlacesAutocompleteRef>(null);
    const i18n = useI18n();

    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [isIncompleteAddress, setIsIncompleteAddress] = useState(false);
    const [isValidatingAddress, setIsValidatingAddress] = useState(false);

    const onDismissPress = useCallback(() => {
        setIsIncompleteAddress(false);
        onDismiss();
    }, [onDismiss]);

    const onLocation = useCallback(async () => {
        if (ref.current?.getCurrentLocation) {
            setIsLocationLoading(true);
            if (!(await ref.current.getCurrentLocation())) {
                setIsLocationLoading(false);
                showSnackbar({ text: i18n.t("errors.geolocation_denied"), mode: "error" });
            }
        }
    }, [i18n, showSnackbar]);

    const renderRow = useCallback((data: GooglePlaceData) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const gpsLocation = data.vicinity;

        return (
            <ListItem
                style={{ alignSelf: "flex-start", flexShrink: 1, width: "100%" }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                icon={data.isCurrentLocation || gpsLocation ? "map-marker" : undefined}
                title={data.structured_formatting?.main_text}
                description={data.description || gpsLocation}
            />
        );
    }, []);

    const handlePlaceSelect = useCallback(
        async (details: GooglePlaceDetail | null) => {
            setIsIncompleteAddress(false);

            if (details) {
                const address = extractAddress(details);

                if (address) {
                    if (!address.locality.postalCode) {
                        setIsIncompleteAddress(true);
                        return;
                    }
                    // When a precise address is required and the selection has no street number,
                    // ask Google whether the street is expected to have one: block only if it does
                    // (streets with no numbers are accepted as-is).
                    if (requiresPreciseAddress && !address.streetNumber) {
                        setIsValidatingAddress(true);
                        const numberMissing = await isStreetNumberMissing(address);
                        setIsValidatingAddress(false);
                        if (numberMissing) {
                            setIsIncompleteAddress(true);
                            return;
                        }
                    }
                    onSelect?.(address);
                } else {
                    console.warn("Unable to extract address from details", details);
                    ref.current?.clear();
                    ref.current?.focus();
                }
            }
        },
        [onSelect, requiresPreciseAddress],
    );

    useEffect(() => {
        if (visible && initialValue) {
            ref.current?.setAddressText(initialValue);
        }
    }, [visible, initialValue]);

    // Autofocus the search field when the chooser opens (iOS, Android, web). Deferred so the modal's
    // open animation has presented the input first: focusing mid-presentation is dropped on native
    // (the keyboard wouldn't rise) and the input may not be mounted yet on web.
    useEffect(() => {
        if (!visible) {
            return;
        }
        const timer = setTimeout(() => ref.current?.focus(), 350);
        return () => clearTimeout(timer);
    }, [visible]);

    return (
        <Modal visible={visible} onClose={onDismissPress} hideCloseButton>
            <GooglePlacesAutocomplete
                ref={ref}
                debounce={SEARCH_DEBOUNCE_MS}
                GooglePlacesSearchQuery={{
                    rankby: "distance",
                }}
                enableHighAccuracyLocation={false}
                placeholder={inputPlaceholder || ""}
                onLocationEnd={() => {
                    setIsLocationLoading(false);
                }}
                onPress={(_, details = null) => void handlePlaceSelect(details)}
                fetchDetails={true}
                onFail={(x) => {
                    console.warn("Google map address lookup failed", x);
                }}
                query={{
                    key: Constants.expoConfig?.extra?.googleApiKey,
                    language,
                    components: allowedCountries
                        ?.slice(0, 5)
                        .map((c) => `country:${c}`)
                        .join("|"),
                    type: "address",
                }}
                styles={{
                    container: {
                        marginHorizontal: 0,
                    },

                    row: {
                        marginVertical: gridSize / 4,
                        paddingVertical: 0,
                        paddingHorizontal: 0,
                        flexDirection: "row",
                        backgroundColor: "transparent",
                    },
                    listView: {
                        backgroundColor: "transparent",
                        alignSelf: "stretch",
                    },
                    separator: {
                        height: 1,
                        backgroundColor: "transparent",
                    },
                }}
                keepResultsAfterBlur
                renderRow={renderRow}
                GooglePlacesDetailsQuery={{
                    fields: "",
                }}
                textInputProps={
                    {
                        InputComp: HeaderField,
                        testID: ADDRESS_SEARCH_INPUT_TEST_ID,
                        label: inputLabel,
                        isLocationLoading,
                        onChangeTextExternal: () => {
                            setIsIncompleteAddress(false);
                        },
                        isIncompleteAddress,
                        onLocation,
                        clearButtonMode: "while-editing",
                        style: {
                            backgroundColor: "white",
                            marginHorizontal: marginSize,
                            paddingRight: 10,
                        },
                        onDismiss: onDismissPress,
                    } as HeaderFieldProps
                }
                isRowScrollable={false}
                enablePoweredByContainer={false}
            />
            {isValidatingAddress && (
                <View
                    style={[
                        StyleSheet.absoluteFill,
                        {
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                        },
                    ]}
                >
                    <ActivityIndicator size="large" />
                </View>
            )}
            <SnackbarZone />
        </Modal>
    );
};
